import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "rsuite";
import Swal from "sweetalert2";
import PageTitle from "../../../layouts/PageTitle";
import { appApi } from "../../../../store/appApi";
import { applyLeaveApi } from "../../../../services/leave";

const LEAVE_TYPES = [
  "Casual Leave",
  "Sick Leave",
  "Earned Leave",
  "Maternity Leave",
  "Paternity Leave",
  "Unpaid Leave",
];
const PERIODS = ["Full Day", "First Half", "Second Half"];

// Reads the calendar date the user actually clicked, in whatever timezone
// the browser is in — no ISO/UTC conversion, so nothing can shift by a day
// on the wire (a raw Date sent as JSON serializes via toISOString(), which
// silently rolls IST local-midnight back to the previous UTC day).
const toDateOnlyString = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const countWorkingDays = (from, to, period) => {
  if (!from || !to || to < from) return 0;
  let count = 0;
  const cur = new Date(from);
  while (cur <= to) {
    count++;
    cur.setDate(cur.getDate() + 1);
  }
  if (period !== "Full Day" && count > 0) return Math.max(count - 0.5, 0.5);
  return count;
};

const getWorkingDaysList = (from, to) => {
  if (!from || !to || to < from) return [];
  const days = [];
  const cur = new Date(from);
  while (cur <= to) {
    days.push({ date: new Date(cur), working: true });
    cur.setDate(cur.getDate() + 1);
  }
  return days;
};

// "HH:MM" → minutes since midnight, or null
const toMinutes = (s) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s || "");
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

// Hour + minute dropdowns — a plain, unambiguous "HH:MM" picker. `<input
// type="time">` renders a stray seconds/AM-PM segment on some browsers and
// fires native "incomplete value" validation; two selects avoid all of that.
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));

const TimeSelect = ({ value, onChange }) => {
  const [h = "", m = ""] = (value || "").split(":");
  // Keep a half-picked value ("09:" / ":30") so the first dropdown doesn't
  // reset while the other is still empty; toMinutes() treats it as invalid.
  const emit = (nh, nm) => onChange(nh || nm ? `${nh}:${nm}` : "");
  return (
    <div className="d-flex align-items-center gap-2">
      <select
        className="form-control"
        value={h}
        onChange={(e) => emit(e.target.value, m)}
      >
        <option value="">HH</option>
        {HOURS.map((x) => (
          <option key={x} value={x}>{x}</option>
        ))}
      </select>
      <span className="fw-bold">:</span>
      <select
        className="form-control"
        value={m}
        onChange={(e) => emit(h, e.target.value)}
      >
        <option value="">MM</option>
        {MINUTES.map((x) => (
          <option key={x} value={x}>{x}</option>
        ))}
      </select>
    </div>
  );
};

const Apply = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    leaveType: "",
    leaveFrom: null,
    leaveTo: null,
    leavePeriod: "Full Day",
    desc: "",
    // Short leave (intimation only — no effect on attendance)
    isShortLeave: false,
    shortLeaveFrom: "",
    shortLeaveTo: "",
  });
  const [loading, setLoading] = useState(false);

  const handle = (field) => (val) =>
    setForm((f) => {
      const next = { ...f, [field]: val };
      // Auto-reset leaveTo if it's before the new leaveFrom
      if (field === "leaveFrom" && next.leaveTo && val && next.leaveTo < val) {
        next.leaveTo = null;
      }
      return next;
    });

  const totalDays = countWorkingDays(
    form.leaveFrom,
    form.leaveTo,
    form.leavePeriod,
  );
  const daysList = getWorkingDaysList(form.leaveFrom, form.leaveTo);

  const startMin = toMinutes(form.shortLeaveFrom);
  const endMin = toMinutes(form.shortLeaveTo);
  const shortHours =
    startMin != null && endMin != null && endMin > startMin
      ? Math.round(((endMin - startMin) / 60) * 100) / 100
      : null;

  const submit = async (e) => {
    e.preventDefault();

    if (form.isShortLeave) {
      if (!form.leaveFrom) {
        Swal.fire(
          "Error",
          "Please select the date for the short leave.",
          "error",
        );
        return;
      }
      if (shortHours == null) {
        Swal.fire("Error", "Please enter a valid From and To time.", "error");
        return;
      }
      if (shortHours < 1 || shortHours > 2) {
        Swal.fire(
          "Error",
          "Short leave must be between 1 and 2 hours.",
          "error",
        );
        return;
      }
      setLoading(true);
      try {
        await applyLeaveApi({
          leaveType: form.leaveType || "Short Leave",
          isShortLeave: true,
          leaveFrom: toDateOnlyString(form.leaveFrom),
          leaveTo: toDateOnlyString(form.leaveFrom),
          shortLeaveFrom: form.shortLeaveFrom,
          shortLeaveTo: form.shortLeaveTo,
          leavePeriod: "Short Leave",
          desc: form.desc,
        });
        dispatch(appApi.util.invalidateTags(["Leaves", "AttendanceCalendar"]));
        Swal.fire(
          "Recorded",
          "Your short leave has been recorded for intimation. It does not affect your attendance.",
          "success",
        ).then(() => navigate("/staff/leave/list"));
      } catch (err) {
        Swal.fire(
          "Error",
          err?.response?.data?.message || "Could not record short leave.",
          "error",
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!form.leaveFrom || !form.leaveTo) {
      Swal.fire("Error", "Please select both From and To dates.", "error");
      return;
    }
    if (totalDays <= 0) {
      Swal.fire("Error", "Leave To must be on or after Leave From.", "error");
      return;
    }
    setLoading(true);
    try {
      await applyLeaveApi({
        leaveType: form.leaveType,
        leaveFrom: toDateOnlyString(form.leaveFrom),
        leaveTo: toDateOnlyString(form.leaveTo),
        leavePeriod: form.leavePeriod,
        totalLeaves: totalDays,
        desc: form.desc,
      });
      dispatch(appApi.util.invalidateTags(["Leaves"]));
      Swal.fire(
        "Applied",
        "Your leave application has been submitted.",
        "success",
      ).then(() => navigate("/staff/leave/list"));
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.message || "Could not apply leave.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Apply Leave" motherMenu="Leave" />
      <div className="row">
        <div className="col-xl-6 col-lg-8 mx-auto">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Leave Application</h4>
            </div>
            <div className="card-body">
              <form onSubmit={submit}>
                {/* Short leave toggle */}
                <div className="form-check form-switch mb-3">
                  <input
                    style={{ width: "60px" }}
                    className="form-check-input"
                    type="checkbox"
                    id="shortLeaveToggle"
                    checked={form.isShortLeave}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, isShortLeave: e.target.checked }))
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor="shortLeaveToggle"
                  >
                    Short leave (1–2 hours) — intimation only, no impact on
                    attendance
                  </label>
                </div>

                {/* Leave Type */}
                <div className="mb-3">
                  <label className="form-label">
                    Leave Type{" "}
                    {!form.isShortLeave && (
                      <span className="text-danger">*</span>
                    )}
                  </label>
                  <select
                    className="form-control"
                    value={form.leaveType}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, leaveType: e.target.value }))
                    }
                    required={!form.isShortLeave}
                  >
                    <option value="">Select type…</option>
                    {LEAVE_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                {form.isShortLeave ? (
                  <>
                    {/* Single date for short leave */}
                    <div className="mb-3">
                      <label className="form-label">
                        Date <span className="text-danger">*</span>
                      </label>
                      <div className="input-hasicon">
                        <DatePicker
                          placeholder="Select date"
                          className="picker-suit"
                          value={form.leaveFrom}
                          onChange={handle("leaveFrom")}
                          oneTap
                        />
                        <div className="icon">
                          <i className="far fa-calendar" />
                        </div>
                      </div>
                    </div>

                    {/* From / To time */}
                    <div className="row mb-3">
                      <div className="col-sm-6">
                        <label className="form-label">
                          From time <span className="text-danger">*</span>
                        </label>
                        <TimeSelect
                          value={form.shortLeaveFrom}
                          onChange={(v) =>
                            setForm((f) => ({ ...f, shortLeaveFrom: v }))
                          }
                        />
                      </div>
                      <div className="col-sm-6">
                        <label className="form-label">
                          To time <span className="text-danger">*</span>
                        </label>
                        <TimeSelect
                          value={form.shortLeaveTo}
                          onChange={(v) =>
                            setForm((f) => ({ ...f, shortLeaveTo: v }))
                          }
                        />
                      </div>
                    </div>

                    {shortHours != null && (
                      <div
                        className={`alert py-2 mb-3 ${
                          shortHours >= 1 && shortHours <= 2
                            ? "alert-info"
                            : "alert-warning"
                        }`}
                      >
                        Duration:{" "}
                        <strong>
                          {shortHours} hour{shortHours === 1 ? "" : "s"}
                        </strong>
                        {(shortHours < 1 || shortHours > 2) &&
                          " — must be between 1 and 2 hours"}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* From / To date pickers */}
                    <div className="row mb-3">
                      <div className="col-sm-6">
                        <label className="form-label">
                          From <span className="text-danger">*</span>
                        </label>
                        <div className="input-hasicon">
                          <DatePicker
                            placeholder="Select date"
                            className="picker-suit"
                            value={form.leaveFrom}
                            onChange={handle("leaveFrom")}
                            oneTap
                          />
                          <div className="icon">
                            <i className="far fa-calendar" />
                          </div>
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <label className="form-label">
                          To <span className="text-danger">*</span>
                        </label>
                        <div className="input-hasicon">
                          <DatePicker
                            placeholder="Select date"
                            className="picker-suit"
                            value={form.leaveTo}
                            onChange={handle("leaveTo")}
                            shouldDisableDate={(date) =>
                              form.leaveFrom ? date < form.leaveFrom : false
                            }
                            oneTap
                          />
                          <div className="icon">
                            <i className="far fa-calendar" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Period */}
                    <div className="mb-3">
                      <label className="form-label">Period</label>
                      <select
                        className="form-control"
                        value={form.leavePeriod}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            leavePeriod: e.target.value,
                          }))
                        }
                      >
                        {PERIODS.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Day breakdown */}
                    {form.leaveFrom && form.leaveTo && daysList.length > 0 && (
                      <div className="mb-3">
                        <div className="alert alert-info py-2 mb-2">
                          Days: <strong>{totalDays}</strong>
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {daysList.map(({ date, working }) => (
                            <div
                              key={date.toISOString()}
                              title={
                                working
                                  ? "Working day"
                                  : "Weekend — not counted"
                              }
                              style={{
                                width: 42,
                                height: 42,
                                borderRadius: 6,
                                background: working ? "#3f9ae022" : "#94a3b822",
                                border: `2px solid ${working ? "#3f9ae0" : "#94a3b8"}`,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 10,
                                opacity: working ? 1 : 0.5,
                              }}
                            >
                              <span style={{ fontWeight: 700, fontSize: 13 }}>
                                {date.getDate()}
                              </span>
                              <span style={{ color: "#888", fontSize: 9 }}>
                                {date.toLocaleString("default", {
                                  weekday: "short",
                                })}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Reason */}
                <div className="mb-3">
                  <label className="form-label">Reason</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={form.desc}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, desc: e.target.value }))
                    }
                    placeholder="Brief reason for leave…"
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={loading}
                >
                  {loading
                    ? "Submitting…"
                    : form.isShortLeave
                      ? "Record Short Leave"
                      : "Submit Application"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Apply;
